import type { EnvironmentVariable, TestRun } from './types';

/** The default environment variables for a custom function. */
export const ENVIRONMENT_VARIABLES_EMPTY_STATE: EnvironmentVariable[] = [
  { name: 'TRANSCEND_API_KEY', value: 'add-your-key', isSecret: true },
  { name: 'MY_API_URL', value: 'https://api.example.com', isSecret: false },
] as const;

/** The default code for a custom function. */
export const CODE_EMPTY_STATE: string = `export default async function customFunction({
  environment,
  payload,
  sdk,
}: CustomFunction.Argument): Promise<void> {
  // This is a sample custom function to respond to a data subject access request.
  // Find the user based on their core identifier
  console.log(payload.coreIdentifier.value);

  // Call a remote API that responds with the data you'd like to upload into Transcend
  // to service a DSR.

  /**
   * At the end of the function, upload the result to Sombra's internal API,
   * using the provided Transcend SDK.
   *
   * @see https://docs.transcend.io/docs/articles/sombra/deploying/customizing-sombra/networking#sombra-customer-ingress
   * @see https://docs.transcend.io/docs/api-reference/POST/v1/data-silo
   */
  await sdk.fetch(\`/v1/data-silo\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      /** The Transcend API key */
      Authorization: \`Bearer \${environment.TRANSCEND_API_KEY}\`,
    },
    body: JSON.stringify({
      profiles: [
        {
          profileData: {
            myDataPoint: 'value1',
            myOtherDataPoint: 45689,
          },
        }
      ],
      status: 'READY',
    }),
  });
}`;

/** The default test payload for a test run. */
export const DEFAULT_PAYLOAD = {
  type: 'CONTACT_OPT_IN',
  dataSubject: {
    type: 'customer',
  },
  isTest: true,
  extras: {
    profile: {
      identifier: '+16125553289',
      type: 'phone',
      id: 'fa0dec69-3c57-47f4-bf36-863fb4d3a219',
      RequestDataSiloId: '3e388e77-9820-468f-8968-06ad66167d18',
    },
    request: {
      details: '',
      id: '8152397f-152e-4c80-896d-03ad9ebea832',
      link: '/request/8152397f-152e-4c80-896d-03ad9ebea832',
      createdAt: '2022-03-04T03:52:38.696Z',
      locale: 'en',
      country: 'US',
      countrySubDivision: 'US-CA',
      origin: 'ADMIN_DASHBOARD',
      attributes: [
        {
          key: 'Department',
          values: ['HR'],
        },
      ],
    },
    organization: {
      id: '497f6eca-6276-4993-bfeb-53cbbbba6f08',
      uri: 'e-shop-it',
      name: 'eShopIt',
    },
    dataSilo: {
      id: '12dd1fd8-b310-4712-ba6b-0e2e29ecd2ac',
      title: 'My Custom Function',
      description: 'Connects to our homegrown API',
      link: 'https://app.transcend.io/infrastructure/integrations/12dd1fd8-b310-4712-ba6b-0e2e29ecd2ac',
    },
    purpose: {
      purpose: 'Marketing',
      enabled: true,
      preferences: [
        {
          topic: 'Frequency',
          choice: {
            selectValue: 'Daily',
          },
        },
        {
          topic: 'Channel',
          choice: {
            selectValues: ['Sms', 'Email'],
          },
        },
        {
          topic: 'GoPaperless',
          choice: {
            booleanValue: true,
          },
        },
      ],
    },
  },
  coreIdentifier: {
    value: 'jane.doe@example.com',
  },
} as const;

export const TEST_RUN_EMPTY_STATE: TestRun = {
  id: undefined,
  status: 'idle',
  logs: undefined,
  timeMs: undefined,
  exitCode: undefined,
};
